import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Search, Download, Package, FileText, Box, IndianRupee, ChevronDown, Save, Coins } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AccountsDashboardPage() {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWO, setSelectedWO] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  // Editable state
  const [yarnRates, setYarnRates] = useState({});
  const [otherCosts, setOtherCosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/accounts/work-orders`, { headers })
      .then(res => setWorkOrders(res.data))
      .catch(() => toast.error("Failed to load work orders"));
  }, []);

  const loadSummary = useCallback(async (woId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/accounts/order-summary/${woId}`, { headers });
      setSummary(res.data);
      // Init editable yarn rates
      const rates = {};
      res.data.yarn_summary.items.forEach(i => { rates[i.yarn_type] = i.rate_per_kg || 0; });
      setYarnRates(rates);
      // Init editable other costs
      setOtherCosts(res.data.other_costs.items.map(i => ({ ...i })));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load summary");
    }
    setLoading(false);
  }, []);

  const saveCosts = async () => {
    if (!selectedWO) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/accounts/order-costs/${selectedWO.id}`, {
        yarn_rates: Object.entries(yarnRates).map(([yarn_type, rate_per_kg]) => ({ yarn_type, rate_per_kg: parseFloat(rate_per_kg) || 0 })),
        other_costs: otherCosts.map(c => ({
          name: c.name,
          qty: parseFloat(c.qty) || 0,
          rate: parseFloat(c.rate) || 0,
          total: getOtherCostTotal(c)
        }))
      }, { headers });
      toast.success("Costs saved!");
      loadSummary(selectedWO.id);
    } catch (err) {
      toast.error("Failed to save costs");
    }
    setSaving(false);
  };

  const extractYarnFromImage = async () => {
    if (!selectedWO) return;
    setExtracting(true);
    try {
      const res = await axios.post(`${API}/api/accounts/extract-yarn-from-image/${selectedWO.id}`, {}, { headers, timeout: 60000 });
      toast.success(`Extracted ${res.data.total_items} yarn types (${res.data.total_kgs} Kg) from ${res.data.source_file}`);
      loadSummary(selectedWO.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to extract yarn from image");
    }
    setExtracting(false);
  };

  const downloadExcel = async () => {
    if (!selectedWO) return;
    try {
      const res = await axios.get(`${API}/api/accounts/order-summary/${selectedWO.id}/excel`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${summary?.wo_number || 'Order'}_Cost_Summary.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (err) {
      toast.error("Failed to download Excel");
    }
  };

  const filteredWOs = workOrders.filter(wo =>
    wo.wo_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    wo.customer_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const fmt = (n) => n != null && n > 0 ? `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-';

  // Calculate yarn cost from editable rates
  const getYarnCost = (item) => {
    const rate = parseFloat(yarnRates[item.yarn_type]) || 0;
    return rate > 0 ? round2(item.quantity_kg * rate) : 0;
  };
  const totalYarnCost = summary ? summary.yarn_summary.items.reduce((sum, i) => sum + getYarnCost(i), 0) : 0;

  // Calculate other cost total per row
  const getOtherCostTotal = (c) => {
    const qty = parseFloat(c.qty) || 0;
    const rate = parseFloat(c.rate) || 0;
    const manual = parseFloat(c.total) || 0;
    if (qty > 0 && rate > 0) return round2(qty * rate);
    return manual;
  };
  const totalOtherCosts = otherCosts.reduce((sum, c) => sum + getOtherCostTotal(c), 0);

  const grandTotal = summary ? round2(totalYarnCost + summary.po_summary.total_value + summary.accessories_summary.total_cost + totalOtherCosts) : 0;

  function round2(n) { return Math.round(n * 100) / 100; }

  const updateOtherCost = (idx, field, value) => {
    const updated = [...otherCosts];
    updated[idx] = { ...updated[idx], [field]: value };
    // If qty or rate changed, clear manual total
    if (field === 'qty' || field === 'rate') {
      const qty = parseFloat(field === 'qty' ? value : updated[idx].qty) || 0;
      const rate = parseFloat(field === 'rate' ? value : updated[idx].rate) || 0;
      if (qty > 0 && rate > 0) {
        updated[idx].total = round2(qty * rate);
      }
    }
    setOtherCosts(updated);
  };

  return (
    <div className="space-y-6" data-testid="accounts-dashboard">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Accounts Dashboard</h1>
          <p className="text-sm text-muted-foreground">Order-wise cost reconciliation</p>
        </div>
        {summary && (
          <div className="flex gap-2">
            <Button onClick={saveCosts} disabled={saving} variant="outline" data-testid="save-costs-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Costs"}
            </Button>
            <Button onClick={downloadExcel} data-testid="download-excel-btn">
              <Download className="w-4 h-4 mr-2" /> Download Excel
            </Button>
          </div>
        )}
      </div>

      {/* Work Order Selector */}
      <Card>
        <CardContent className="py-4">
          <div className="relative max-w-lg">
            <label className="text-sm font-medium mb-1 block">Select Work Order</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by WO number or customer name..."
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                className="pl-9 pr-8"
                data-testid="wo-search-input"
              />
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            </div>
            {showDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {filteredWOs.slice(0, 50).map(wo => (
                  <div key={wo.id} className="px-3 py-2 hover:bg-muted cursor-pointer text-sm border-b last:border-0" onClick={() => {
                    setSelectedWO(wo);
                    setSearchQuery(`${wo.wo_number} — ${wo.customer_name || ''}`);
                    setShowDropdown(false);
                    loadSummary(wo.id);
                  }}>
                    <span className="font-medium">{wo.wo_number}</span>
                    {wo.customer_name && <span className="text-muted-foreground ml-2">— {wo.customer_name}</span>}
                  </div>
                ))}
                {filteredWOs.length === 0 && (
                  <div className="px-3 py-3 text-sm text-muted-foreground text-center">No work orders found</div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-8 text-muted-foreground">Loading cost summary...</div>}

      {summary && !loading && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Card>
              <CardContent className="py-3">
                <p className="text-xs text-muted-foreground">Yarn Cost</p>
                <p className="text-lg font-bold" data-testid="yarn-total">{totalYarnCost > 0 ? fmt(totalYarnCost) : fmt(0)}</p>
                <p className="text-xs text-muted-foreground">{summary.yarn_summary.total_kgs} Kgs</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3">
                <p className="text-xs text-muted-foreground">PO Value ({summary.po_summary.total_pos} POs)</p>
                <p className="text-lg font-bold" data-testid="po-total">{fmt(summary.po_summary.total_value)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3">
                <p className="text-xs text-muted-foreground">Accessories</p>
                <p className="text-lg font-bold" data-testid="acc-total">{fmt(summary.accessories_summary.total_cost)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3">
                <p className="text-xs text-muted-foreground">Other Costs</p>
                <p className="text-lg font-bold" data-testid="other-total">{totalOtherCosts > 0 ? fmt(totalOtherCosts) : fmt(0)}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 text-white col-span-2 lg:col-span-1">
              <CardContent className="py-3">
                <p className="text-xs text-white/60">Grand Total</p>
                <p className="text-xl font-bold" data-testid="grand-total">{fmt(grandTotal) !== '-' ? fmt(grandTotal) : '₹0.00'}</p>
              </CardContent>
            </Card>
          </div>

          {/* Yarn Cost Table — Editable Rate */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle className="text-base flex items-center gap-2"><Package className="w-4 h-4 text-blue-600" /> Yarn Cost Summary</CardTitle>
                <div className="flex items-center gap-2">
                  {summary.yarn_summary.source === 'image' && (
                    <Badge variant="secondary" className="text-xs">From: {summary.yarn_summary.source_file || 'Image'}</Badge>
                  )}
                  <Button variant="outline" size="sm" onClick={extractYarnFromImage} disabled={extracting} data-testid="extract-yarn-btn">
                    {extracting ? "Extracting..." : "Extract from Image"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead>Yarn Type</TableHead>
                    <TableHead>Color</TableHead>
                    <TableHead className="text-right">Qty (Kg)</TableHead>
                    <TableHead className="text-right w-32">Rate/Kg (₹)</TableHead>
                    <TableHead className="text-right">Cost (₹)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.yarn_summary.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{item.yarn_type}</TableCell>
                      <TableCell className="text-sm">{item.color || '-'}</TableCell>
                      <TableCell className="text-right font-mono">{item.quantity_kg}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number" step="0.01" className="w-28 h-8 text-right ml-auto"
                          value={yarnRates[item.yarn_type] || ''}
                          onChange={e => setYarnRates({ ...yarnRates, [item.yarn_type]: e.target.value })}
                          placeholder="Enter rate"
                          data-testid={`yarn-rate-${idx}`}
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {getYarnCost(item) > 0 ? fmt(getYarnCost(item)) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {summary.yarn_summary.items.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">No yarn requirements found</TableCell></TableRow>
                  )}
                  {summary.yarn_summary.items.length > 0 && (
                    <TableRow className="bg-muted/20 font-bold">
                      <TableCell>Total</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right font-mono">{summary.yarn_summary.total_kgs} Kg</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right font-mono">{totalYarnCost > 0 ? fmt(totalYarnCost) : '-'}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* PO Summary Table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-green-600" /> Purchase Orders Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead>PO Number</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Subtotal</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.po_summary.items.map((po, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{po.po_number}</TableCell>
                      <TableCell>{po.vendor_name}</TableCell>
                      <TableCell className="text-sm">{po.date}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(po.subtotal)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(po.tax)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{fmt(po.total)}</TableCell>
                      <TableCell className="text-center"><Badge variant="secondary" className="text-xs">{po.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                  {summary.po_summary.items.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">No POs found</TableCell></TableRow>
                  )}
                  {summary.po_summary.items.length > 0 && (
                    <TableRow className="bg-muted/20 font-bold">
                      <TableCell colSpan={5}>Total ({summary.po_summary.total_pos} POs)</TableCell>
                      <TableCell className="text-right font-mono">{fmt(summary.po_summary.total_value)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Vendor Breakdown */}
          {summary.po_summary.vendor_breakdown.length > 0 && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Vendor-wise Breakdown</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow className="bg-muted/30"><TableHead>Vendor</TableHead><TableHead className="text-right">PO Count</TableHead><TableHead className="text-right">Total Value</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {summary.po_summary.vendor_breakdown.map((v, idx) => (
                      <TableRow key={idx}><TableCell className="font-medium">{v.vendor}</TableCell><TableCell className="text-right">{v.po_count}</TableCell><TableCell className="text-right font-mono font-semibold">{fmt(v.total)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Accessories Cost */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Box className="w-4 h-4 text-purple-600" /> Accessories Cost</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead>Item</TableHead><TableHead className="text-right">Qty Ordered</TableHead><TableHead className="text-right">Qty Received</TableHead><TableHead>Unit</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Cost</TableHead><TableHead>PO</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.accessories_summary.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{item.name}</TableCell><TableCell className="text-right font-mono">{item.qty_ordered}</TableCell><TableCell className="text-right font-mono">{item.qty_received}</TableCell><TableCell>{item.unit}</TableCell><TableCell className="text-right font-mono">{fmt(item.rate)}</TableCell><TableCell className="text-right font-mono font-semibold">{fmt(item.cost)}</TableCell><TableCell className="text-sm">{item.po_number}</TableCell>
                    </TableRow>
                  ))}
                  {summary.accessories_summary.items.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">No accessories found</TableCell></TableRow>
                  )}
                  {summary.accessories_summary.items.length > 0 && (
                    <TableRow className="bg-muted/20 font-bold"><TableCell colSpan={5}>Total</TableCell><TableCell className="text-right font-mono">{fmt(summary.accessories_summary.total_cost)}</TableCell><TableCell></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Other Costs — Editable */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Coins className="w-4 h-4 text-amber-600" /> Other Costs</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Enter Qty × Rate for auto-calculation, OR enter Total directly</p>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead>Cost Head</TableHead>
                    <TableHead className="text-right w-28">Qty</TableHead>
                    <TableHead className="text-right w-28">Rate (₹)</TableHead>
                    <TableHead className="text-right w-32">Total (₹)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {otherCosts.map((c, idx) => {
                    const autoCalc = (parseFloat(c.qty) || 0) > 0 && (parseFloat(c.rate) || 0) > 0;
                    return (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{c.name}</TableCell>
                        <TableCell className="text-right">
                          <Input type="number" step="0.01" className="w-24 h-8 text-right ml-auto" value={c.qty || ''} onChange={e => updateOtherCost(idx, 'qty', e.target.value)} placeholder="-" data-testid={`other-qty-${idx}`} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input type="number" step="0.01" className="w-24 h-8 text-right ml-auto" value={c.rate || ''} onChange={e => updateOtherCost(idx, 'rate', e.target.value)} placeholder="-" data-testid={`other-rate-${idx}`} />
                        </TableCell>
                        <TableCell className="text-right">
                          {autoCalc ? (
                            <span className="font-mono font-semibold text-green-700">{fmt(getOtherCostTotal(c))}</span>
                          ) : (
                            <Input type="number" step="0.01" className="w-28 h-8 text-right ml-auto" value={c.total || ''} onChange={e => updateOtherCost(idx, 'total', e.target.value)} placeholder="Enter total" data-testid={`other-total-${idx}`} />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="bg-muted/20 font-bold">
                    <TableCell colSpan={3}>Total Other Costs</TableCell>
                    <TableCell className="text-right font-mono">{totalOtherCosts > 0 ? fmt(totalOtherCosts) : '-'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Save Button at Bottom */}
          <div className="flex justify-end gap-3">
            <Button onClick={saveCosts} disabled={saving} size="lg" data-testid="save-costs-bottom">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save All Costs"}
            </Button>
          </div>
        </>
      )}

      {!summary && !loading && (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <IndianRupee className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p className="text-lg font-medium">Select a Work Order to view cost summary</p>
            <p className="text-sm mt-1">Search and pick any order above</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
